/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useDesktop } from '@revenuewell/front-end-bundle';
import React, { createContext, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import usePhone from './use-phone';
import { usePhoneLock } from '../use-phone-lock/use-phone-lock';

interface IPhoneWarnings {
  handleStartCall: (phone: string) => void;
  hideWarning: () => void;
  toggleActions: () => void;
  showWarning: boolean;
  showActions: boolean;
  phoneNumber?: string;
  isLoading: boolean;
  warning?: 'notcreated' | 'notconfigured' | 'nomicrophone' | 'nomicpermissions' | 'device-in-use';
}

const PhoneWarningsContext = createContext<IPhoneWarnings | null>(null);

export function PhoneWarningsProvider({ children }: React.PropsWithChildren<{}>): ReactElement | null {
  const [showWarning, setShowWarning] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>();
  const [showActions, setShowActions] = useState(false);
  const { isDesktopApplication, hideWindow } = useDesktop();
  const [hasMicrophonePermissions, setHasMicrophonePermissions] = useState(isDesktopApplication());
  const {
    deviceControl: { deviceStatus },
    webPhoneStatus: { microphoneExists }
  } = usePhone();
  const { deviceLockStatus } = usePhoneLock();

  useEffect(() => {
    if (isDesktopApplication()) return;
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    const handlePermissionChange = (event: any) => {
      if(event.target.name === 'microphone' || event.target.name === 'audio_capture')
        setHasMicrophonePermissions(event.target.state === 'granted')
    };
    
    const temp: any = { name: 'microphone' };
    const status = await navigator.permissions.query(temp as PermissionDescriptor);
    setHasMicrophonePermissions(status.state === 'granted')
    status.addEventListener('change', handlePermissionChange);
  }

  const warning = useMemo(() => {
    if (deviceLockStatus === 'in-use-by-other-client')  return 'device-in-use';

    if (deviceStatus === 'notcreated') return 'notcreated';

    if (deviceStatus === 'creating') return 'notcreated';

    if (deviceStatus === 'notconfigured') return 'notconfigured';

    if (microphoneExists === false) return 'nomicrophone';

    if (!hasMicrophonePermissions) return 'nomicpermissions';
  }, [deviceStatus, deviceLockStatus, microphoneExists, hasMicrophonePermissions]);

  const isLoading = useMemo(() => {
    if(!deviceLockStatus) return false;
    return ['creating', 'loading'].includes(deviceStatus) || (['current-client-pending'].includes(deviceLockStatus)); 
  }, [deviceStatus, deviceLockStatus]);

  const hideWarning = useCallback(() => {
    setShowWarning(false);
    setPhoneNumber(undefined);
    if (isDesktopApplication()) hideWindow();
  }, []);

  const handleStartCall = (phoneNumber: string) => {
    if (warning) {
      setPhoneNumber(phoneNumber);
      setShowWarning(true);
    }
  };

  const toggleActions = () => setShowActions((a) => !a);

  return (
    <PhoneWarningsContext.Provider
      value={{ isLoading, warning, hideWarning, phoneNumber, showWarning, handleStartCall, showActions, toggleActions }}
    >
      {children}
    </PhoneWarningsContext.Provider>
  );
}

export const usePhoneWarnings = (): IPhoneWarnings => {
  const context = useContext(PhoneWarningsContext);
  if (!context) throw new Error('usePhoneWarnings must be used within an PhoneWarningsContext');
  return context;
};
